import { useEffect, useState } from 'react';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { toast } from 'react-toastify';

function getNotificationPermissionState() {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'denied';
  }

  if (localStorage.getItem('notificationBannerDismissed') === 'true') {
    return 'granted';
  }

  return Notification.permission;
}

export function useGeofenceNotifications(currentUser, passportAccessToken) {
  const [notificationPermission, setNotificationPermission] = useState(() => getNotificationPermissionState());

  useEffect(() => {
    const syncPermission = () => {
      const nextPermission = getNotificationPermissionState();
      console.log('[Notification Debug] permission sync:', nextPermission, 'dismissed=', localStorage.getItem('notificationBannerDismissed'));
      setNotificationPermission(nextPermission);
    };

    syncPermission();
    window.addEventListener('focus', syncPermission);
    document.addEventListener('visibilitychange', syncPermission);

    return () => {
      window.removeEventListener('focus', syncPermission);
      document.removeEventListener('visibilitychange', syncPermission);
    };
  }, []);

  const requestPermission = async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;

    try {
      const permissionResult = await Notification.requestPermission();
      const nextPermission = permissionResult || Notification.permission;
      console.log('[Notification Debug] requestPermission result:', nextPermission);
      setNotificationPermission(nextPermission);
      localStorage.setItem('notificationBannerDismissed', 'true');
    } catch (error) {
      console.warn('Chrome blocked the prompt (likely due to HTTP), but we hid the banner anyway.', error);
      setNotificationPermission(Notification.permission);
      localStorage.setItem('notificationBannerDismissed', 'true');
    }
  };

  useEffect(() => {
    if (!currentUser || !passportAccessToken) {
      return;
    }

    const userId = currentUser?.user?.id || currentUser?.id || currentUser?.user_id;
    
    if (!userId) {
      return;
    }

    window.Pusher = Pusher;
    Pusher.logToConsole = true;

    const echo = new Echo({
      broadcaster: 'pusher',
      key: '1bb714350e7e2ec6e90a',
      cluster: 'ap2',
      wsHost: new URL(global.api_url).hostname,
      wssPort: 443,
      forceTLS: true,
      disableStats: true,
      enabledTransports: ['ws'],
      authEndpoint: `${global.api_url}/broadcasting/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${passportAccessToken}`,
        },
      },
    });

    echo.connector.pusher.connection.bind('connected', () => {
      console.log("✅ WebSocket Connected to Soketi!");
    });
    
    echo.connector.pusher.connection.bind('error', (err) => {
      console.error("❌ WebSocket Connection Error:", err);
    });

    const channelName = `App.Models.User.${userId}`;
    console.log(`Subscribing to private channel: ${channelName}`);
    
    const channel = echo.private(channelName);

    channel.listen('.geofence.stop', (payload) => {
      console.log('📡 Payload:', payload);

    const currentStatus = (payload.status || '').toLowerCase();
    const targetStatuses = ['stop', 'parked', 'idle'];

    const actionText = targetStatuses.some(status => currentStatus.includes(status))
      ? 'reached' 
      : 'is near';

      const message = `${payload.vehicleNumber} ${actionText} ${payload.geofenceName ?? ''}`.trim();
      
      window.dispatchEvent(new CustomEvent('geofence-alert', { detail: { message, payload } }));

      toast.info(message, {
        position: "top-right",
        autoClose: 5000,
      });

      if (Notification.permission === 'granted') {
        new Notification('Geofence Alert', { body: message });
      }
    });

    return () => {
      channel.stopListening('.geofence.stop');
      echo.leave(channelName);
      echo.disconnect();
    };
  }, [currentUser, passportAccessToken]);

  return { notificationPermission, requestPermission };
}